import React, { useEffect, useState } from 'react';
import { planningAPI } from '../services/api';
import StageFenetrePromotion from './StageFenetrePromotion';
import FenetreModeleCourriel from './FenetreModeleCourriel';
import './SuiviStage.css';

/**
 * PLANNING ANNUEL — « ENVOYER LE PLANNING » (lot PA.3 ; maquette « envoi » et « envoi 2 », validées le 28/10)
 * ===========================================================================================================
 * Prépare l'envoi d'un MODÈLE D'EXPORT puis ouvre LA FENÊTRE D'ENVOI GROUPÉ COMMUNE (StageFenetrePromotion), avec ce
 * que le Planning annuel lui demande en plus : les GROUPES (une formation × étudiants / maîtres ; les enseignants), DEUX
 * TEXTES (étudiants, maîtres) et le bouton « Modèles de courriel… ». Le PDF est fabriqué par l'écran d'export qui
 * l'ouvre (`fabriquerPdf`), au moment de lancer, et joint à chaque message. Sans serveur d'envoi : la liste « un par
 * un » (la messagerie s'ouvre pour une personne ; le PDF se télécharge une fois, à joindre à la main).
 */
const erreurDe = (e) => e?.response?.data?.error || e?.message || "L'opération a échoué";
const enErreurFenetre = (message, detail = null) => Object.assign(new Error(message), { response: { data: { error: message, detail } } });
export const base64De = (pdf) => { const b = new Uint8Array(pdf.output('arraybuffer')); let s = ''; for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode.apply(null, b.subarray(i, i + 0x8000)); return btoa(s); };

/** Ce que l'éditeur montre pour chaque modèle (types `pa_*`, v95). */
const COMMUNES = ['{annee}', '{titre_document}', '{departement}', '{nom_utilisateur}'];
const MODELES = {
    pa_etudiant: { icone: 'fa-user-graduate', destinataire: 'Chaque étudiant ou apprenti (un message personnel)',
        sousTitre: 'Le planning de SA formation, en pièce jointe', variables: ['{prenom}', '{nom}', '{formation}', ...COMMUNES] },
    pa_maitre: { icone: 'fa-briefcase', destinataire: "Chaque maître de stage ou d'apprentissage (un message, même s'il en accueille plusieurs)",
        sousTitre: '{apprentis} : la liste de SES apprentis ou stagiaires', variables: ['{maitre}', '{apprentis}', '{formation}', ...COMMUNES] },
    pa_enseignant: { icone: 'fa-chalkboard-user', destinataire: 'Chaque enseignant du département (un message personnel)',
        sousTitre: 'Les dates de l\'année, en pièce jointe', variables: ['{prenom}', '{nom}', ...COMMUNES] }
};
const LIBELLES_CC = { responsables: 'Cc : responsables (Équipe) — un récapitulatif', assistantes: 'Cc : assistantes (Équipe) — un récapitulatif' };
const NOTE = "Les variables sont remplacées pour CHAQUE destinataire au moment de l'envoi. Le PDF du planning est joint à chaque message.";

/**
 * L'envoi d'un modèle d'export. `modele` : { id, titre } ; `fabriquerPdf()` : le PDF de ce modèle (objet jsPDF), avec
 * le planning du moment.
 */
export default function PlanningAnnuelEnvoi({ departementId, modele, fabriquerPdf, modifiable, onFermer, onTermine }) {
    const [brouillon, setBrouillon] = useState(null);
    const [erreur, setErreur] = useState(null);
    const [editeur, setEditeur] = useState(null);       // { typeMail, resoudre }
    useEffect(() => {
        let vivant = true;
        planningAPI.preparerEnvoi(modele.id, departementId)
            .then((r) => { if (vivant) setBrouillon(r.data); })
            .catch((e) => { if (vivant) setErreur({ message: erreurDe(e), detail: e?.response?.data?.detail || null }); });
        return () => { vivant = false; };
    }, [modele.id, departementId]);

    const pdfBase64 = () => {
        try { return base64De(fabriquerPdf()); }
        catch (e) { throw enErreurFenetre('Le PDF du planning n\'a pas pu être fabriqué : ' + erreurDe(e)); }
    };
    const api = {
        lancer: async (dep, { edition, essai }) => planningAPI.lancerEnvoi(modele.id, dep, { edition, essai, pdf: pdfBase64() }),
        etat: (dep, id) => planningAPI.etatEnvoi(dep, id)
    };
    /** « Modèles de courriel… » : l'éditeur s'ouvre ; s'il a enregistré, le texte recomposé par le serveur revient. */
    const onModeles = (typeMail) => new Promise((resoudre) => setEditeur({ typeMail, resoudre }));
    const fermerEditeur = async (change) => {
        const { typeMail, resoudre } = editeur;
        setEditeur(null);
        if (!change) { resoudre(null); return; }
        try {
            const b = (await planningAPI.preparerEnvoi(modele.id, departementId)).data;
            const t = Object.values(b.textes || {}).find((x) => x.type_mail === typeMail);
            resoudre(t ? { sujet: t.sujet, html: t.html } : null);
        } catch (e) { resoudre(null); }
    };

    if (erreur) {
        return (
            <div className="stg-root"><div className="stg-modale-fond" role="dialog" aria-modal="true" onClick={onFermer}>
                <div className="stg-modale" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                    <div className="stg-modale-header"><i className="fa-solid fa-paper-plane" /> <span>Envoyer « {modele.titre} »</span></div>
                    <div className="stg-modale-body"><div className="fe-retour fe-retour-ko" style={{ margin: 0 }}><i className="fa-solid fa-triangle-exclamation" /><span><b>{erreur.message}</b>{erreur.detail ? <> — {erreur.detail}</> : null}</span></div></div>
                    <div className="stg-modale-pied"><button type="button" className="btn btn-outline btn-sm" onClick={onFermer}>Fermer</button></div>
                </div>
            </div></div>
        );
    }
    if (!brouillon) {
        return <div className="stg-root"><div className="stg-modale-fond"><div className="stg-modale" style={{ maxWidth: 420 }}><div className="stg-modale-body"><i className="fa-solid fa-circle-notch stg-tourne" /> Préparation des messages…</div></div></div></div>;
    }
    return (
        <div className="stg-root pl-envoi-hote">
            {brouillon.mode === 'smtp'
                ? <StageFenetrePromotion brouillon={brouillon} departementId={departementId} api={api} onFermer={onFermer} onTermine={onTermine || (() => {})}
                                         onModeles={modifiable ? onModeles : null} />
                : <SecoursUnParUn brouillon={brouillon} departementId={departementId} modele={modele} pdfBase64={pdfBase64} onFermer={onFermer} onTermine={onTermine || (() => {})} />}
            {editeur && (
                <FenetreModeleCourriel departementId={departementId} modifiable={modifiable} api={planningAPI}
                    charger={async () => ((await planningAPI.modelesCourriel(departementId)).data.modeles_mail || []).find((x) => x.type_mail === editeur.typeMail)}
                    presentation={{ ...MODELES[editeur.typeMail], note: NOTE, libellesCc: LIBELLES_CC, portee: "Le modèle est celui du département, gardé d'un envoi à l'autre, avec ses versions." }}
                    onFermer={fermerEditeur} />
            )}
        </div>
    );
}

/** Sans serveur d'envoi : chaque message s'ouvre dans la messagerie de la personne ; le PDF se télécharge une fois. */
function SecoursUnParUn({ brouillon, departementId, modele, pdfBase64, onFermer, onTermine }) {
    const [lignes, setLignes] = useState(() => brouillon.etudiants.map((l) => ({ ...l })));
    const [faits, setFaits] = useState({});
    const [erreur, setErreur] = useState(null);
    const [occupe, setOccupe] = useState(null);
    const ouvrir = async (l) => {
        setOccupe(l.dossier_id); setErreur(null);
        try {
            const t = brouillon.textes?.[l.genre] || brouillon;
            const r = await planningAPI.mailtoEnvoi(modele.id, departementId, {
                cle: l.dossier_id,
                edition: { etudiants: [{ dossier_id: l.dossier_id, adresse: l.adresse, coche: true }], cc: (brouillon.cc || []).map((x) => x.adresse), cci: [],
                           textes: { [l.genre]: { sujet: t.sujet, html: t.html } } }
            });
            setFaits((f) => ({ ...f, [l.dossier_id]: true }));
            window.location.href = r.data.mailto;
        } catch (e) { setErreur({ message: erreurDe(e), detail: e?.response?.data?.detail || null }); }
        finally { setOccupe(null); }
    };
    const telecharger = () => {
        setErreur(null);
        try {
            const a = document.createElement('a');
            a.href = 'data:application/pdf;base64,' + pdfBase64();
            a.download = brouillon.nom_pdf || 'Planning.pdf';
            document.body.appendChild(a); a.click(); a.remove();
        } catch (e) { setErreur({ message: erreurDe(e) }); }
    };
    const fermer = () => { if (Object.keys(faits).length) onTermine(true); onFermer(); };
    return (
        <div className="stg-modale-fond" role="dialog" aria-modal="true" onClick={fermer}>
            <div className="stg-modale fe-fenetre" style={{ maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
                <div className="stg-modale-header fe-titre"><i className="fa-solid fa-envelope-open" /> <span>{brouillon.titre} — dans votre messagerie, un par un</span></div>
                <div className="stg-modale-body fe-corps">
                    <div className="fe-retour fe-retour-ko" style={{ marginTop: 0 }}><i className="fa-solid fa-triangle-exclamation" /><span><b>Le serveur d'envoi n'est pas configuré</b> pour ce département (Administration → Envoi des courriels). Téléchargez le PDF une fois, puis « Ouvrir » prépare chaque message dans votre messagerie (objet, texte, destinataire) : joignez-y le PDF. Le journal note « préparé ».</span></div>
                    <button type="button" className="btn btn-outline btn-sm" style={{ margin: '8px 0' }} onClick={telecharger}><i className="fa-solid fa-download" /> Télécharger « {brouillon.nom_pdf} »</button>
                    <table className="fe-table">
                        <thead><tr><th>Destinataire</th><th>Adresse</th><th /></tr></thead>
                        <tbody>{lignes.filter((l) => l.coche || !l.adresse).map((l) => {
                            const i = lignes.indexOf(l);
                            return (
                                <tr key={l.dossier_id}>
                                    <td><b>{l.a_libelle || `${l.nom} ${l.prenom}`}</b>{l.info && <div className="fe-aide" style={{ margin: 0 }}>{l.info}</div>}</td>
                                    <td><input className="input-sm fe-adr-input" value={l.adresse} placeholder="aucune adresse — saisissez-en une" onChange={(e) => setLignes((ls) => ls.map((x, k) => (k === i ? { ...x, adresse: e.target.value } : x)))} /></td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <button type="button" className="btn btn-green btn-sm" disabled={!!occupe || !l.adresse} onClick={() => ouvrir(l)}>{faits[l.dossier_id] ? <><i className="fa-solid fa-check" /> Rouvrir</> : <><i className="fa-solid fa-envelope-open" /> Ouvrir</>}</button>
                                    </td>
                                </tr>
                            );
                        })}</tbody>
                    </table>
                    {erreur && <div className="fe-retour fe-retour-ko"><i className="fa-solid fa-circle-xmark" /><span><b>{erreur.message}</b>{erreur.detail ? <> {erreur.detail}</> : null}</span></div>}
                </div>
                <div className="stg-modale-pied"><span className="fe-aide" style={{ marginRight: 'auto' }}>{Object.keys(faits).length} préparé{Object.keys(faits).length > 1 ? 's' : ''}</span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={fermer}>Fermer</button></div>
            </div>
        </div>
    );
}
